import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DatePickerComponent } from '@mode/shared/ui';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { formatInUTC } from '@mode/shared/util-js';
import { EncContinuousDateFilterField, EncFilterField } from '@mode-vegas/vegas';

@Component({
  selector: 'mode-date-range',
  standalone: true,
  imports: [CommonModule, DatePickerComponent, FormsModule],
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss'],
})
export class DateRangeComponent implements OnInit, OnChanges {
  @Input() filter!: EncContinuousDateFilterField;
  @Input() dataMinTimestamp: number | undefined | null;
  @Input() dataMaxTimestamp: number | undefined | null;
  @Input() isLoading = true;

  @Output() filterChange = new EventEmitter<EncFilterField>();
  @Output() isInvalid = new EventEmitter<boolean>();

  // Currently selected min/max values for the filter
  startTimestamp: number | null = null;
  endTimestamp: number | null = null;

  dataMinDate: Date | null = null;
  dataMaxDate: Date | null = null;

  filterStartDate: Date | null = null;
  filterEndDate: Date | null = null;

  // Formatted dates to display in the date inputs
  startDisplayDate = 'Loading';
  endDisplayDate = 'Loading';

  ngOnInit(): void {
    // Set the display values for the date inputs
    this.startDisplayDate = this.formatDisplayDate(this.startTimestamp, this.dataMinTimestamp);
    this.endDisplayDate = this.formatDisplayDate(this.endTimestamp, this.dataMaxTimestamp);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter?.currentValue) {
      this.startTimestamp = this.filter.range.min;
      this.endTimestamp = this.filter.range.max;

      this.filterStartDate = this.startTimestamp == null ? null : new Date(this.startTimestamp);
      this.filterEndDate = this.endTimestamp == null ? null : new Date(this.endTimestamp);

      this.startDisplayDate = this.formatDisplayDate(this.startTimestamp, this.dataMinTimestamp);
      this.endDisplayDate = this.formatDisplayDate(this.endTimestamp, this.dataMaxTimestamp);
    }

    if (changes.dataMinTimestamp && this.dataMinTimestamp != null) {
      this.dataMinDate = new Date(this.dataMinTimestamp);
      this.startDisplayDate = this.formatDisplayDate(this.startTimestamp, this.dataMinTimestamp);
    }

    if (changes.dataMaxTimestamp && this.dataMaxTimestamp != null) {
      this.dataMaxDate = new Date(this.dataMaxTimestamp);
      this.endDisplayDate = this.formatDisplayDate(this.endTimestamp, this.dataMaxTimestamp);
    }
  }

  onFilterStartDateChange(date: Date | null) {
    const normalizedDate = date != null ? new Date(date.getTime()).setUTCHours(0, 0, 0, 0) : null;
    this.startTimestamp = normalizedDate != null && !Number.isNaN(normalizedDate) ? normalizedDate : null;
    this.updateDateFilter();
  }

  onFilterEndDateChange(date: Date | null) {
    const normalizedDate = date != null ? new Date(date.getTime()).setUTCHours(23, 59, 59, 999) : null;
    this.endTimestamp = normalizedDate != null && !Number.isNaN(normalizedDate) ? normalizedDate : null;
    this.updateDateFilter();
  }

  formatDisplayDate(filterDate: number | null | undefined, dataData: number | null | undefined) {
    const formatDate = filterDate ?? dataData ?? 'Loading';

    if (typeof formatDate === 'string') {
      return 'Loading';
    }
    return formatInUTC(new Date(formatDate), 'M/dd/yyy');
  }

  updateDateFilter() {
    if (this.validate()) {
      this.filterChange.emit({
        ...this.filter,
        range: {
          min: this.startTimestamp as number,
          max: this.endTimestamp as number,
        },
      });
    }
  }

  onBlurInput() {
    this.validate();
  }

  validate() {
    // Validate that the min < max
    if (this.startTimestamp !== null && this.endTimestamp !== null && this.startTimestamp > this.endTimestamp) {
      this.isInvalid.emit(true);
      return false;
    }
    this.isInvalid.emit(false);
    return true;
  }
}
